import "./tournament-team-add-edit-player.scss";
import React, { useCallback, useEffect, useState } from "react";
import { GSForm, GSSidePanelPage } from "golfstatus_react_components";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { useMatch, useNavigate } from "react-router-dom";
import { getAddPlayerForm } from "../../../forms/TeamForm";
import { useDispatch, useSelector } from "react-redux";
import {
  getTournamentTeam,
  saveTournamentTeam,
  saveTournamentTeamPlayer,
  selectCurrentTeam,
  setCurrentTeam,
} from "../../../reducers/teamsSlice";
import { useFormValidation } from "../../../hooks/formHooks";
import { selectCurrentTournament } from "../../../reducers/tournamentSlice";

//Name the component
const TournamentTeamAddEditPlayer = (props) => {
  const defaultAvatar = "https://api.golfstatus.dev/images/user_round.png";

  const match = useMatch("/:tournamentID/teams/add/players/add/:playerID");
  const matchNewEdit = useMatch(
    "/:tournamentID/teams/add/players/edit/:playerID"
  );
  const matchEditAdd = useMatch(
    "/:tournamentID/teams/:teamID/edit/players/add/:playerID"
  );
  const matchEdit = useMatch(
    "/:tournamentID/teams/:teamID/edit/players/edit/:playerID"
  );

  const currentTeam = useSelector(selectCurrentTeam);
  const currentTournament = useSelector(selectCurrentTournament);
  const isProAmTourney = currentTournament?.singleFormat === "pro_am";

  const [player, setPlayer] = useState({
    tempID: match?.params?.playerID ?? matchEditAdd?.params?.playerID,
    avatar: defaultAvatar,
    firstName: "",
    lastName: "",
    mainContact: (currentTeam.contactName ?? "") === "",
    flag: "default",
  });

  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    if (matchEdit && currentTeam?.id !== matchEdit?.params?.teamID) {
      dispatch(getTournamentTeam(matchEdit.params.teamID));
    }
  }, [dispatch, matchEdit, currentTeam?.id]);

  const setPlayerCallback = useCallback(
    (team) => {
      if (matchEdit?.params?.playerID && team) {
        setPlayer(
          team?.players?.find?.(
            (p) => (p.id ?? p.tempID) === matchEdit?.params?.playerID
          )
        );
      } else if (matchNewEdit?.params?.playerID && team) {
        setPlayer(
          team?.players?.find?.(
            (p) => (p.id ?? p.tempID) === matchNewEdit?.params?.playerID
          )
        );
      }
    },
    [matchNewEdit?.params?.playerID, matchEdit?.params?.playerID]
  );

  useEffect(() => {
    setPlayerCallback(currentTeam);
  }, [currentTeam, setPlayerCallback]);

  const [context, isValid, setIsValid] = useFormValidation(false);

  context.updatePlayer = (value, property) => {
    setIsValid(true);
    let updatePlayer = { ...player };
    updatePlayer[property] = value;
    setPlayer(updatePlayer);
  };

  const isPro = () => {
    if (player?.flag === "pro") {
      return true;
    }
    if (player?.flag === "default") {
      return false;
    }
  };

  context.isPro = isPro();
  context.isProAmTourney = isProAmTourney;

  context.togglePro = () => {
    if (player.firstName && player.lastName) {
      setIsValid(true);
    }

    if (player?.flag === "pro") {
      setPlayer({
        ...player,
        flag: "default",
      });
    }
    if (player?.flag === "default") {
      setPlayer({
        ...player,
        flag: "pro",
      });
    }
  };

  context.validationFailed = () => {
    setIsValid(false);
  };

  context.submit = () => {
    if (isValid) {
      addPlayer();
    }
  };

  const addPlayer = () => {
    if (match || matchEditAdd) {
      const updatedTeam = {
        ...currentTeam,
        contactName:
          (currentTeam.contactName ?? "") === ""
            ? `${player.firstName} ${player.lastName}`
            : currentTeam.contactName,
        email:
          (currentTeam.email ?? "") === "" ? player.email : currentTeam.email,
        phone:
          (currentTeam.phone ?? "") === "" ? player.phone : currentTeam.phone,
        players: [...(currentTeam.players ?? []), player],
      };
      dispatch(setCurrentTeam(updatedTeam));
      if (matchEditAdd) {
        dispatch(saveTournamentTeam(updatedTeam));
      }
    }
    if (matchEdit) {
      if (player.id) {
        dispatch(
          saveTournamentTeamPlayer({
            ...player,
            name: `${player.firstName} ${player.lastName}`,
            tournamentTeam: currentTeam,
          })
        );
      }
      swapPlayer(matchEdit?.params?.playerID);
    }
    if (matchNewEdit) {
      swapPlayer(matchNewEdit?.params?.playerID);
    }
    leftNavigation();
  };

  const removePlayer = () => {
    if (matchNewEdit) {
      dispatch(
        setCurrentTeam({
          ...currentTeam,
          players: currentTeam.players.filter(
            (p) => (p.id ?? p.tempID) !== matchNewEdit?.params?.playerID
          ),
        })
      );
      navigate(-1, {replace: true});
    } else {
      navigate("remove");
    }
  };

  const swapPlayer = (playerID) => {
    const playerIndex = currentTeam?.players?.findIndex?.(
      (p) => (p.id ?? p.tempID) === playerID
    );
    const newPlayers = [...currentTeam.players];
    newPlayers[playerIndex ?? playerID] = {
      ...player,
      name: `${player.firstName} ${player.lastName}`,
    };
    dispatch(setCurrentTeam({ ...currentTeam, players: newPlayers }));
  };

  const getDrawerActions = () => {
    let actions = [
      {
        name: "Save",
        isDisabled: !isValid,
        action: addPlayer,
        type: "black",
      },
      { name: "cancel", action: leftNavigation, type: "grey" },
    ];
    if (matchEdit || matchNewEdit) {
      actions.push({
        name: "Remove Player",
        action: removePlayer,
        type: "transparent red",
      });
    }
    return actions;
  };

  const leftNavigation = () => {
    navigate(-1);
  };

  const getNavigation = () => {
    //Add Title to your component
    return {
      title: `Player Details`,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const getContactAction = () => {
    return [
      {
        actionType: "toggle",
        value: player?.mainContact ?? false,
        pageActionProps: {
          label: "Main Contact?",
          falseDescription: "No",
          rowReverse: true,
          onClick: (e) =>
            context.updatePlayer(!player?.mainContact, "mainContact"),
          trueDescription: "Yes",
          value: player?.mainContact,
        },
      },
    ];
  };

  const getActionBar = () => {
    return {
      formTitle: match ? "Add Player" : "Edit Player",
      formActions: getContactAction(),
    };
  };

  const getContent = () => {
    return (
      <GSForm
        {...getActionBar()}
        formSections={getAddPlayerForm(player, context)}
      ></GSForm>
    );
  };

  const getDrawer = () => {
    return {
      actions: getDrawerActions(),
    };
  };

  return (
    //name the component tag
    <tournament-team-add-edit-player>
      <GSSidePanelPage
        header={getNavigation()}
        content={getContent()}
        drawer={getDrawer()}
      ></GSSidePanelPage>
    </tournament-team-add-edit-player>
  );
};

//export the new name
export default TournamentTeamAddEditPlayer;
